import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Breadcrumb,
    Card,
    Col,
    Form,
    InputGroup,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import { Button } from 'primereact/button';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/user-gantt.css';
import '../../assets/css/capacity-dashboard.css';
import { isActiveTeamMember } from './ganttHelpers';

/* ── Heat helpers ─────────────────────────────────────────────────────────── */
const HEAT_META = {
    critical: { label: 'Critical', bg: '#dc3545', text: '#fff' },
    high:     { label: 'High',     bg: '#fd7e14', text: '#fff' },
    medium:   { label: 'Medium',   bg: '#ffc107', text: '#212529' },
    ok:       { label: 'OK',       bg: '#198754', text: '#fff' },
};

const heatBadge = (level) => {
    const m = HEAT_META[level] || HEAT_META.ok;
    return (
        <span
            className="cap-heat-badge"
            style={{ background: m.bg, color: m.text }}
        >
            {m.label}
        </span>
    );
};

const PRIORITY_COLOR = {
    Urgent: '#dc3545',
    High:   '#fd7e14',
    Medium: '#0d6efd',
    Low:    '#6c757d',
};

const HORIZON_OPTIONS = [
    { label: '7 days',  value: 7 },
    { label: '14 days', value: 14 },
    { label: '1 month', value: 30 },
    { label: '3 months',value: 90 },
    { label: 'Auto (from deadlines)', value: '' },
];

/* ── Utilization bar ──────────────────────────────────────────────────────── */
const UtilBar = ({ pct, level }) => {
    const meta = HEAT_META[level] || HEAT_META.ok;
    const isLow = pct < 30;
    return (
        <div
            className={`cap-util-bar-wrap${isLow ? ' is-low' : ''}`}
            title={`${pct}% utilized`}
        >
            <div
                className="cap-util-bar-fill"
                style={{
                    width: `${Math.min(pct, 100)}%`,
                    background: meta.bg,
                }}
            />
            {pct > 100 && (
                <div
                    className="cap-util-bar-overflow"
                    style={{ width: `${Math.min(pct - 100, 50)}%` }}
                />
            )}
            <span className="cap-util-bar-label">{pct}%</span>
        </div>
    );
};

/* ── Stat card ────────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, accent }) => (
    <Card className="h-100 cap-stat-card" style={{ borderTop: `3px solid ${accent}` }}>
        <Card.Body className="py-3 px-4">
            <div className="cap-stat-value" style={{ color: accent }}>{value}</div>
            <div className="cap-stat-label">{label}</div>
            {sub && <div className="cap-stat-sub">{sub}</div>}
        </Card.Body>
    </Card>
);

/* ── Main component ───────────────────────────────────────────────────────── */
const CapacityDashboard = () => {
    const { accessToken, userId } = useAuth();
    const navigate = useNavigate();

    const [users, setUsers]       = useState([]);
    const [health, setHealth]     = useState(null);
    const [overdue, setOverdue]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const [refreshing, setRefreshing] = useState(false); // true only on re-fetches after first load
    const [hasLoaded, setHasLoaded]   = useState(false); // flips true after first successful load
    const [error, setError]       = useState('');

    /* filters */
    const [horizon, setHorizon]         = useState('');      // '' = auto
    const [search, setSearch]           = useState('');
    const [heatFilter, setHeatFilter]   = useState('all');
    const [sortBy, setSortBy]           = useState('utilization_pct');
    const [sortDir, setSortDir]         = useState('desc');
    const [groupByHeat, setGroupByHeat] = useState(false);

    /* overdue filters */
    const [overdueSearch, setOverdueSearch]   = useState('');
    const [overduePriority, setOverduePriority] = useState('all');
    const [overdueSort, setOverdueSort]       = useState('due_date');
    const [showAllOverdue, setShowAllOverdue] = useState(false);

    /* active tab */
    const [tab, setTab] = useState('team'); // 'team' | 'overdue'

    /* ── Fetch ──────────────────────────────────────────────────────────── */
    const load = useCallback(async () => {
        if (!accessToken) return;
        if (hasLoaded) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const capacityParams = { limit: 200 };
            if (horizon) capacityParams.horizon_days = horizon;

            const [capRes, healthRes, overdueRes] = await Promise.all([
                axios.get(`${config.apiBASEURL}/TaskBoardRoutes/reports/capacity`, {
                    headers, params: capacityParams,
                }),
                axios.get(`${config.apiBASEURL}/TaskBoardRoutes/reports/execution-health`, { headers }),
                axios.get(`${config.apiBASEURL}/TaskBoardRoutes/reports/overdue`, {
                    headers, params: { limit: 200 },
                }),
            ]);
            setUsers((capRes.data?.users || []).filter(isActiveTeamMember));
            setHealth(healthRes.data || null);
            setOverdue(overdueRes.data?.cards || []);
            setHasLoaded(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load capacity report');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [accessToken, horizon, hasLoaded]);

    useEffect(() => { load(); }, [load]);

    /* ── Derived data ───────────────────────────────────────────────────── */
    const mySnapshot = users.find((u) => String(u.user_id) === String(userId));

    const filteredUsers = useMemo(() => {
        let list = [...users];
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter((u) => (u.name || '').toLowerCase().includes(q));
        }
        if (heatFilter !== 'all') list = list.filter((u) => u.heat_level === heatFilter);
        list.sort((a, b) => {
            const av = a[sortBy] ?? 0;
            const bv = b[sortBy] ?? 0;
            const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
            return sortDir === 'desc' ? -cmp : cmp;
        });
        return list;
    }, [users, search, heatFilter, sortBy, sortDir]);

    const hasTableFilters = Boolean(search.trim()) || heatFilter !== 'all';

    const memberCountLabel = useMemo(() => {
        if (hasTableFilters) {
            return `Showing ${filteredUsers.length} of ${users.length} team members`;
        }
        return `${users.length} team member${users.length === 1 ? '' : 's'}`;
    }, [filteredUsers.length, users.length, hasTableFilters]);

    const groupedUsers = useMemo(() => {
        if (!groupByHeat) return null;
        const order = ['critical', 'high', 'medium', 'ok'];
        const map = {};
        order.forEach((k) => { map[k] = []; });
        filteredUsers.forEach((u) => {
            const key = u.heat_level in map ? u.heat_level : 'ok';
            map[key].push(u);
        });
        return order.filter((k) => map[k].length > 0).map((k) => ({ heat: k, users: map[k] }));
    }, [filteredUsers, groupByHeat]);

    const filteredOverdue = useMemo(() => {
        let list = [...overdue];
        if (overdueSearch.trim()) {
            const q = overdueSearch.toLowerCase();
            list = list.filter(
                (c) =>
                    (c.title || '').toLowerCase().includes(q) ||
                    (c.board_name || '').toLowerCase().includes(q)
            );
        }
        if (overduePriority !== 'all') list = list.filter((c) => c.priority === overduePriority);
        list.sort((a, b) => {
            if (overdueSort === 'due_date') {
                return new Date(a.due_date || 0) - new Date(b.due_date || 0);
            }
            if (overdueSort === 'priority') {
                const order = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
                return (order[a.priority] ?? 9) - (order[b.priority] ?? 9);
            }
            return (a.title || '').localeCompare(b.title || '');
        });
        return list;
    }, [overdue, overdueSearch, overduePriority, overdueSort]);

    const displayedOverdue = showAllOverdue ? filteredOverdue : filteredOverdue.slice(0, 10);

    /* ── Sort header helper ─────────────────────────────────────────────── */
    const SortTh = ({ field, label }) => {
        const active = sortBy === field;
        return (
            <th
                className="cap-sort-th"
                onClick={() => {
                    if (active) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
                    else { setSortBy(field); setSortDir('desc'); }
                }}
            >
                {label}{' '}
                <span className="cap-sort-icon">
                    {active ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}
                </span>
            </th>
        );
    };

    /* ── User table rows ────────────────────────────────────────────────── */
    const UserRow = ({ user }) => (
        <tr key={user.user_id} className={user.heat_level === 'critical' ? 'cap-row-critical' : ''}>
            <td>
                <strong>{user.name || `User #${user.user_id}`}</strong>
            </td>
            <td><UtilBar pct={user.utilization_pct} level={user.heat_level} /></td>
            <td>{heatBadge(user.heat_level)}</td>
            <td className="text-end">{user.required_hours}h</td>
            <td className="text-end">{user.available_hours}h</td>
            <td className="text-end">{user.active_card_count}</td>
            <td className="text-end">
                {user.conflict_count > 0
                    ? <span className="text-danger fw-semibold">{user.conflict_count}</span>
                    : <span className="text-success">0</span>
                }
            </td>
            <td className="text-end">
                {user.spill_hours > 0
                    ? <span className="text-warning fw-semibold">+{user.spill_hours}h</span>
                    : <span className="text-muted">—</span>
                }
            </td>
            <td>
                <Button
                    label="Timeline"
                    size="small"
                    className="border-0 py-0 px-2"
                    severity="secondary"
                    onClick={() =>
                        navigate(`/Graphe/dashboard/admin_user_gantt?user_id=${user.user_id}`)
                    }
                />
            </td>
        </tr>
    );

    const UserTableBody = ({ list }) =>
        list.map((user) => <UserRow key={user.user_id} user={user} />);

    /* ── Render ─────────────────────────────────────────────────────────── */
    return (
        <Row className="body_content user-gantt-page cap-dashboard">
            <Col md={12} className="mb-3">
                <Breadcrumb>
                    <Breadcrumb.Item
                        onClick={() => navigate('/Graphe/dashboard/boards_list')}
                        style={{ cursor: 'pointer' }}
                    >
                        Boards
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>Capacity Dashboard</Breadcrumb.Item>
                </Breadcrumb>
            </Col>

            {/* ── Initial full-page loader (first fetch only) ── */}
            {loading && (
                <Col md={12} className="text-center py-5">
                    <Spinner animation="border" className="me-2" />
                    <div className="mt-3 text-muted">Loading capacity data…</div>
                </Col>
            )}

            {/* ── Top controls — only after first load ── */}
            {!loading && (
                <Col md={12} className="mb-3">
                    <div className="cap-topbar">
                        <div className="cap-topbar-left">
                            <strong className="cap-page-title">Team Capacity</strong>
                            {health && !refreshing && (
                                <span className="cap-generated">
                                    Updated {moment().format('DD MMM HH:mm')}
                                </span>
                            )}
                            {refreshing && (
                                <span className="cap-generated">
                                    <Spinner animation="border" size="sm" className="me-1" style={{ width: 12, height: 12, borderWidth: 2 }} />
                                    Refreshing…
                                </span>
                            )}
                        </div>
                        <div className="cap-topbar-right d-flex gap-2 align-items-center flex-wrap">
                            <Form.Select
                                size="sm"
                                style={{ width: 170 }}
                                value={horizon}
                                onChange={(e) => setHorizon(e.target.value)}
                                title="Available hours horizon"
                            >
                                {HORIZON_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Button
                                icon="pi pi-refresh"
                                size="small"
                                severity="secondary"
                                className="border-0"
                                loading={refreshing}
                                onClick={load}
                                tooltip="Refresh"
                            />
                        </div>
                    </div>
                </Col>
            )}

            {!loading && error && (
                <Col md={12}><Alert variant="danger">{error}</Alert></Col>
            )}

            {!loading && !error && health && (
                <>
                    {/* ── Stat cards ── */}
                    <Col xs={6} md={3} className="mb-3">
                        <StatCard
                            label="Health score"
                            value={`${health.health_score}%`}
                            sub={health.health_score >= 80 ? 'Good' : health.health_score >= 50 ? 'Needs attention' : 'Critical'}
                            accent={health.health_score >= 80 ? '#198754' : health.health_score >= 50 ? '#ffc107' : '#dc3545'}
                        />
                    </Col>
                    <Col xs={6} md={3} className="mb-3">
                        <StatCard
                            label="Active cards"
                            value={health.active_cards}
                            sub={`${health.on_hold} on hold`}
                            accent="#0d6efd"
                        />
                    </Col>
                    <Col xs={6} md={3} className="mb-3">
                        <StatCard
                            label="Overdue"
                            value={health.overdue_cards}
                            sub="cards past deadline"
                            accent={health.overdue_cards > 0 ? '#dc3545' : '#198754'}
                        />
                    </Col>
                    <Col xs={6} md={3} className="mb-3">
                        <StatCard
                            label="Pending review"
                            value={health.pending_review}
                            sub={`${health.in_changes} in changes`}
                            accent="#fd7e14"
                        />
                    </Col>

                    {/* ── My load ── */}
                    {mySnapshot && (
                        <Col md={12} className="mb-3">
                            <Card className="cap-my-load-card">
                                <Card.Body className="d-flex flex-wrap gap-4 align-items-center py-3 px-4">
                                    <div>
                                        <div className="cap-my-load-label">Your load</div>
                                        <UtilBar
                                            pct={mySnapshot.utilization_pct}
                                            level={mySnapshot.heat_level}
                                        />
                                    </div>
                                    <div className="cap-my-load-stats">
                                        <span>{mySnapshot.required_hours}h required</span>
                                        <span className="text-muted mx-1">/</span>
                                        <span>{mySnapshot.available_hours}h available</span>
                                        <span className="ms-3">{mySnapshot.active_card_count} cards</span>
                                        {mySnapshot.conflict_count > 0 && (
                                            <span className="ms-3 text-danger">
                                                ⚡ {mySnapshot.conflict_count} conflict(s)
                                            </span>
                                        )}
                                        {mySnapshot.spill_hours > 0 && (
                                            <span className="ms-3 text-warning">
                                                +{mySnapshot.spill_hours}h spill
                                            </span>
                                        )}
                                    </div>
                                    <Button
                                        label="My timeline"
                                        icon="pi pi-chart-bar"
                                        severity="info"
                                        size="small"
                                        className="border-0 ms-auto"
                                        onClick={() => navigate('/Graphe/dashboard/user_gantt')}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    )}

                    {/* ── Tab switcher ── */}
                    <Col md={12} className="mb-3">
                        <div className="cap-tabs">
                            <button
                                className={`cap-tab ${tab === 'team' ? 'is-active' : ''}`}
                                onClick={() => setTab('team')}
                            >
                                Team capacity
                                <span className="cap-tab-count">{users.length}</span>
                            </button>
                            <button
                                className={`cap-tab ${tab === 'overdue' ? 'is-active' : ''}`}
                                onClick={() => setTab('overdue')}
                            >
                                Overdue cards
                                {overdue.length > 0 && (
                                    <span className="cap-tab-count is-danger">{overdue.length}</span>
                                )}
                            </button>
                        </div>
                    </Col>

                    {/* ── Team capacity tab ── */}
                    {tab === 'team' && (
                        <Col md={12} className="mb-3">
                            <Card>
                                <Card.Body>
                                    {/* filter bar */}
                                    <div className="cap-filter-bar mb-3">
                                        <InputGroup size="sm" style={{ maxWidth: 220 }}>
                                            <InputGroup.Text>
                                                <i className="pi pi-search" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder="Search name…"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                        </InputGroup>

                                        <Form.Select
                                            size="sm"
                                            style={{ width: 140 }}
                                            value={heatFilter}
                                            onChange={(e) => setHeatFilter(e.target.value)}
                                        >
                                            <option value="all">All levels</option>
                                            {Object.entries(HEAT_META).map(([k, v]) => (
                                                <option key={k} value={k}>{v.label}</option>
                                            ))}
                                        </Form.Select>

                                        <Form.Check
                                            type="switch"
                                            id="group-heat"
                                            label="Group by heat"
                                            className="ms-2 align-self-center"
                                            checked={groupByHeat}
                                            onChange={(e) => setGroupByHeat(e.target.checked)}
                                        />

                                        <span className="ms-auto text-muted small align-self-center">
                                            {memberCountLabel}
                                            {horizon ? ` · ${horizon}-day horizon` : ''}
                                        </span>
                                    </div>

                                    {filteredUsers.length === 0 ? (
                                        <p className="text-muted">No members match your filters.</p>
                                    ) : (
                                        <div className="table-responsive">
                                            <Table size="sm" className="cap-table align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <SortTh field="name" label="Assignee" />
                                                        <th style={{ minWidth: 160 }}>Utilization</th>
                                                        <th>Level</th>
                                                        <SortTh field="required_hours" label="Required" />
                                                        <SortTh field="available_hours" label="Available" />
                                                        <SortTh field="active_card_count" label="Cards" />
                                                        <SortTh field="conflict_count" label="Conflicts" />
                                                        <SortTh field="spill_hours" label="Spill" />
                                                        <th />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {groupByHeat
                                                        ? groupedUsers.map((group) => (
                                                              <React.Fragment key={group.heat}>
                                                                  <tr className="cap-group-header">
                                                                      <td colSpan={9}>
                                                                          {heatBadge(group.heat)}
                                                                          <span className="ms-2 text-muted small">
                                                                              {group.users.length} member(s)
                                                                          </span>
                                                                      </td>
                                                                  </tr>
                                                                  <UserTableBody list={group.users} />
                                                              </React.Fragment>
                                                          ))
                                                        : <UserTableBody list={filteredUsers} />
                                                    }
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    )}

                    {/* ── Overdue tab ── */}
                    {tab === 'overdue' && (
                        <Col md={12} className="mb-3">
                            <Card>
                                <Card.Body>
                                    <div className="cap-filter-bar mb-3">
                                        <InputGroup size="sm" style={{ maxWidth: 220 }}>
                                            <InputGroup.Text>
                                                <i className="pi pi-search" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder="Search card or board…"
                                                value={overdueSearch}
                                                onChange={(e) => setOverdueSearch(e.target.value)}
                                            />
                                        </InputGroup>

                                        <Form.Select
                                            size="sm"
                                            style={{ width: 130 }}
                                            value={overduePriority}
                                            onChange={(e) => setOverduePriority(e.target.value)}
                                        >
                                            <option value="all">All priorities</option>
                                            {['Urgent', 'High', 'Medium', 'Low'].map((p) => (
                                                <option key={p} value={p}>{p}</option>
                                            ))}
                                        </Form.Select>

                                        <Form.Select
                                            size="sm"
                                            style={{ width: 150 }}
                                            value={overdueSort}
                                            onChange={(e) => setOverdueSort(e.target.value)}
                                        >
                                            <option value="due_date">Sort: Due date</option>
                                            <option value="priority">Sort: Priority</option>
                                            <option value="title">Sort: Title</option>
                                        </Form.Select>

                                        <span className="ms-auto text-muted small align-self-center">
                                            {filteredOverdue.length} overdue card(s)
                                        </span>
                                    </div>

                                    {filteredOverdue.length === 0 ? (
                                        <p className="text-muted mb-0">No overdue cards.</p>
                                    ) : (
                                        <>
                                            <div className="table-responsive">
                                                <Table size="sm" className="cap-table align-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Card</th>
                                                            <th>Board</th>
                                                            <th>Priority</th>
                                                            <th>Due date</th>
                                                            <th>Overdue by</th>
                                                            <th>Assignees</th>
                                                            <th />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayedOverdue.map((card) => {
                                                            const daysAgo = card.due_date
                                                                ? moment().diff(moment(card.due_date), 'days')
                                                                : null;
                                                            return (
                                                                <tr key={card.card_id}>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link btn-sm p-0 text-start"
                                                                            onClick={() =>
                                                                                navigate(
                                                                                    `/Graphe/dashboard/kanbanboard/${card.board_id}?card=${card.card_id}`
                                                                                )
                                                                            }
                                                                        >
                                                                            {card.title}
                                                                        </button>
                                                                    </td>
                                                                    <td className="text-muted small">{card.board_name || '—'}</td>
                                                                    <td>
                                                                        {card.priority ? (
                                                                            <span
                                                                                className="cap-priority-dot"
                                                                                style={{
                                                                                    background:
                                                                                        PRIORITY_COLOR[card.priority] ||
                                                                                        '#6c757d',
                                                                                }}
                                                                            >
                                                                                {card.priority}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-muted">—</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="small">
                                                                        {card.due_date
                                                                            ? moment(card.due_date).format('DD MMM YYYY, HH:mm')
                                                                            : '—'}
                                                                    </td>
                                                                    <td>
                                                                        {daysAgo != null && daysAgo > 0 ? (
                                                                            <Badge bg="danger">{daysAgo}d overdue</Badge>
                                                                        ) : daysAgo === 0 ? (
                                                                            <Badge bg="warning" text="dark">Due today</Badge>
                                                                        ) : (
                                                                            <span className="text-muted">—</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="small text-muted">
                                                                        {(card.members || [])
                                                                            .map((m) => m.name)
                                                                            .filter(Boolean)
                                                                            .join(', ') || '—'}
                                                                    </td>
                                                                    <td>
                                                                        <Button
                                                                            label="Open"
                                                                            size="small"
                                                                            severity="secondary"
                                                                            className="border-0 py-0 px-2"
                                                                            onClick={() =>
                                                                                navigate(
                                                                                    `/Graphe/dashboard/kanbanboard/${card.board_id}?card=${card.card_id}`
                                                                                )
                                                                            }
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                            {filteredOverdue.length > 10 && (
                                                <div className="text-center mt-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm"
                                                        onClick={() => setShowAllOverdue((v) => !v)}
                                                    >
                                                        {showAllOverdue
                                                            ? 'Show less'
                                                            : `Show all ${filteredOverdue.length} overdue cards`}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                </>
            )}
        </Row>
    );
};

export default CapacityDashboard;
